import { jsx } from "react/jsx-runtime";
import { createElement } from "react";
import ModalButton from "../../buttons/ModalButton/ModalButton.js";
import Table from "../../dataDisplay/Table/Table.js";
import Image from "../../media/Image/Image.js";
import TextLink from "../../navigation/TextLink/TextLink.js";
import { log } from "../../../utilities/index.js";
import { domToReact } from "html-react-parser";
import { v4 } from "uuid";
import Heading from "../Heading/Heading.js";
import List from "../List/List.js";
import Paragraph from "../Paragraph/Paragraph.js";
const cssStyleParser = (css) => {
  if (!css) {
    return {};
  }
  const styleObject = {};
  const properties = css.split(";").map((property) => property.trim()).filter(Boolean);
  properties.forEach((property) => {
    const [key, value] = property.split(":").map((part) => part.trim());
    const camelCaseKey = key.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
    styleObject[camelCaseKey] = value;
  });
  return styleObject;
};
const resolveNode = (node, content, variant, condensed, tracker) => {
  var _a;
  const props = { ...node == null ? void 0 : node.attribs, style: cssStyleParser((_a = node == null ? void 0 : node.attribs) == null ? void 0 : _a.style) };
  const type = node.name || node.type;
  switch (type) {
    case "h1":
    case "h2":
    case "h3":
    case "h4": {
      const extractText = (nodes) => nodes.map((child) => {
        if (child.type === "text") {
          return child.data;
        }
        if (child.children) {
          return extractText(child.children);
        }
        return "";
      }).join("");
      const text = extractText(node.children);
      return /* @__PURE__ */ createElement(
        Heading,
        {
          ...props,
          key: v4(),
          text,
          type: node.name,
          variant,
          generateAnchor: node.name === "h2"
        }
      );
    }
    case "strong":
    case "s":
    case "b": {
      return /* @__PURE__ */ createElement(
        Paragraph,
        {
          inherit: true,
          ...props,
          condensed,
          component: "span",
          key: v4(),
          text: content,
          strong: true,
          variant,
          mb: 0
        }
      );
    }
    case "italic":
    case "i":
    case "em": {
      return /* @__PURE__ */ createElement(
        Paragraph,
        {
          inherit: true,
          ...props,
          condensed,
          component: "span",
          key: v4(),
          text: content,
          fs: "italic",
          variant,
          mb: 0
        }
      );
    }
    case "text": {
      return /* @__PURE__ */ createElement(
        Paragraph,
        {
          inherit: true,
          ...props,
          condensed,
          component: "span",
          key: v4(),
          text: content,
          variant,
          mb: 0
        }
      );
    }
    case "p":
      return /* @__PURE__ */ createElement(
        Paragraph,
        {
          ...props,
          key: v4(),
          text: content,
          variant,
          condensed,
          mb: 0
        }
      );
    case "span": {
      if (node.attribs["data-type"] && node.attribs["data-type"] === "modal") {
        return /* @__PURE__ */ jsx(
          ModalButton,
          {
            variant: "link",
            title: node.attribs["data-title"],
            modalContent: node.attribs["data-content"],
            buttonText: content
          }
        );
      }
      return /* @__PURE__ */ createElement(
        Paragraph,
        {
          inherit: true,
          ...props,
          component: "span",
          key: v4(),
          text: content,
          variant,
          condensed,
          mb: 0
        }
      );
    }
    case "li":
    case "ol":
    case "ul": {
      const recursive = (data) => {
        if (data.data === "\n    " || data.data === "\n") {
          return null;
        }
        if (data.type === "text") {
          return /* @__PURE__ */ jsx("span", { children: data.data.replace(/\n\s+/g, "") }, v4());
        }
        if (data.name === "strong" || data.name === "b" || data.name === "s") {
          return /* @__PURE__ */ jsx(
            Paragraph,
            {
              inherit: true,
              ...props,
              component: "span",
              strong: true,
              text: data.children.map((child) => recursive(child)),
              variant,
              condensed,
              mb: 0
            },
            v4()
          );
        }
        if (data.name === "italic" || data.name === "i" || data.name === "em") {
          return /* @__PURE__ */ jsx(
            Paragraph,
            {
              inherit: true,
              ...props,
              component: "span",
              fs: "italic",
              text: data.children.map((child) => recursive(child)),
              variant,
              condensed,
              mb: 0
            },
            v4()
          );
        }
        if (data.name === "a") {
          const text = data.children.map((child) => recursive(child));
          if (data.attribs.href) {
            return /* @__PURE__ */ jsx(
              TextLink,
              {
                href: data.attribs.href,
                variant: "inline",
                color: variant,
                condensed,
                target: data.attribs.target ? data.attribs.target : "",
                text
              },
              v4()
            );
          }
          log({
            message: `The Anchor tag: ${text} was found without an href attribute.`,
            type: "error",
            tracker
          });
          return /* @__PURE__ */ jsx("div", { ...props }, v4());
        }
        return data.children.map((child) => recursive(child));
      };
      const items = node.children.filter((child) => (child == null ? void 0 : child.name) === "li").map((child) => {
        const result = child.children.map((grandChildren) => {
          const recursived = recursive(grandChildren);
          return recursived;
        }).filter((x) => x);
        return { text: result };
      });
      return /* @__PURE__ */ createElement(
        List,
        {
          ...props,
          key: v4(),
          withPadding: true,
          type: node.name === "ul" ? "unordered" : "ordered",
          items
        }
      );
    }
    case "a": {
      if (props.href) {
        return /* @__PURE__ */ createElement(
          TextLink,
          {
            ...props,
            key: v4(),
            text: content,
            variant: "inline",
            color: variant,
            condensed
          }
        );
      }
      log({
        message: `The Anchor tag: ${content} was found without an href attribute.`,
        type: "error",
        tracker
      });
      return /* @__PURE__ */ jsx("div", { ...props });
    }
    case "figure": {
      const image = node.children.find((x) => x.name === "img");
      if (!image) {
        log({
          message: "a figure was found, but couldn't find an image inside it. this is not supposed to happen",
          type: "error",
          tracker
        });
        return null;
      }
      const { width, height } = image.attribs;
      return /* @__PURE__ */ createElement(
        Image,
        {
          ...props,
          ...image.attribs,
          key: v4(),
          width,
          height,
          fit: "cover"
        }
      );
    }
    case "img": {
      const { width } = props.style;
      const { height } = props.style;
      const sizes = "50vw";
      return /* @__PURE__ */ createElement(Image, { ...props, key: v4(), width, height, fit: "cover", sizes });
    }
    case "table":
      return /* @__PURE__ */ createElement(Table, { ...props, key: v4(), condensed }, content);
    case "br":
      return /* @__PURE__ */ createElement("br", { ...props, key: v4() });
    case "div":
      return /* @__PURE__ */ jsx("div", { ...props, children: content });
    case "article":
      return /* @__PURE__ */ jsx("article", { ...props, children: content });
    case "section":
      return /* @__PURE__ */ jsx("section", { ...props, children: content });
    default:
      log({
        type: "warning",
        message: `The tag ${node.name} was not mapped in th Html component to a Figurine variant. 
        A standard html component will be rendered.`,
        tracker
      });
      return /* @__PURE__ */ jsx("div", { ...props });
  }
};
const replacer = (variant, condensed, tracker) => {
  const options = {
    replace: (node) => {
      if (node.type === "tag") {
        const content = node.children ? domToReact(node.children, options) : node.data ? node.data : "";
        return resolveNode(node, content, variant, condensed, tracker);
      }
      return false;
    }
  };
  return options;
};
export {
  replacer
};
